/**
 * TrustCard — single trust-claim card primitive for the
 * "Privacy by design" / "Trusted by" sections on marketing
 * surfaces.
 *
 * What this is for:
 *   Each privacy claim is a small icon + title + one-paragraph body.
 *   The card is a single source of truth for the visual treatment
 *   so future surfaces (e.g. a district-IT microsite, a
 *   compliance page) get the same look without forking styles.
 *
 * Implementation notes:
 *   - Pure presentational. No router / analytics imports.
 *   - The illustration slot is a React node so the parent can pass
 *     any inline SVG (or icon font, or image) without coupling
 *     TrustCard to a specific illustration set.
 *   - The `.trust-card` CSS class owns the navy-on-mint visual
 *     treatment; see `web/src/index.css` for the gradient / border.
 */
import type { ReactElement, ReactNode } from 'react';

export interface TrustCardProps {
  /** Visual identifier — a 40–60px square (icon or simple illustration). */
  illustration: ReactNode;
  /** Title — short, sentence-case (e.g. "No PHI to AI"). */
  title: string;
  /** Body — one short paragraph (≤ 200 chars). Plain text, no markup. */
  body: string;
  /** Optional test id for surfaces that pin layouts via tests. */
  testId?: string;
}

export function TrustCard({
  illustration,
  title,
  body,
  testId,
}: TrustCardProps): ReactElement {
  return (
    <article className="trust-card" data-testid={testId}>
      <div className="trust-card-illustration" aria-hidden="true">
        {illustration}
      </div>
      <h3 className="trust-card-title">{title}</h3>
      <p className="trust-card-body">{body}</p>
    </article>
  );
}

export default TrustCard;